import Axios from "axios";
import RootStore from "src/stores";
import { WP_HOST_URL } from "src/constants/config";
import * as WPTypes from "src/types/wp";

export type Params<Key extends string = string> = {
  readonly [key in Key]: string | undefined;
};

interface IParams {
  params: Params<string>;
}

export const isCategoryResponse = (
  responseData: WPTypes.FetchCategoryResponseEntity[],
): responseData is WPTypes.WPCategory[] => {
  return (
    Array.isArray(responseData) &&
    responseData.length > 0 &&
    !!responseData[0]?.taxonomy &&
    responseData[0]?.taxonomy === "category"
  );
};

export const isPostResponse = (
  responseData: WPTypes.FetchPostResponseEntity[],
): responseData is WPTypes.WPPost[] => {
  return (
    Array.isArray(responseData) &&
    responseData.length > 0 &&
    !!responseData[0].type &&
    responseData[0]?.type === "post"
  );
};

export const isPostCommentResponse = (
  responseData: WPTypes.FetchPostResponseEntity[],
): responseData is WPTypes.WPPostComment[] => {
  return (
    Array.isArray(responseData) &&
    responseData.length > 0 &&
    !!responseData[0].type! &&
    responseData[0]?.type === "comment"
  );
};

export async function fetchData<T extends WPTypes.FetchResponseEntity>(
  url: string,
): Promise<WPTypes.FetchResponse<T>> {
  const { ecosystemStore } = RootStore;

  await ecosystemStore.setLoadingPosts(true);

  const hostUrl = WP_HOST_URL;
  const urlRoot = "/wp-json/wp/v2/";

  const fullUrl = `${hostUrl}${urlRoot}${url}`;

  console.log("fetch full url", fullUrl);

  try {
    const response = await Axios.get(fullUrl);

    console.log("axios respose", response.data);
    console.log("axios respose headers", response.headers);
    const res = {
      responseData: response.data.length ? response.data : [],
      responseHeader: response.headers ? response.headers : {},
    };
    await ecosystemStore.setLoadingPosts(false);
    return res;
  } catch (error) {
    console.log("could not fetch posts", error);
    await ecosystemStore.setLoadingPosts(false);
    return {
      responseData: [] as unknown as T[],
      responseHeader: {},
    };
  }
}

export async function fetchPostComments(
  url: string,
): Promise<WPTypes.WPPostCommentsResponse> {
  try {
    const fetchResponse = await fetchData<WPTypes.WPPostComment>(url);
    console.log("fetchResponse post comment", fetchResponse);

    const postCommentsData = fetchResponse.responseData;

    if (isPostCommentResponse(postCommentsData)) {
      const total = fetchResponse.responseHeader["x-wp-total"];

      return {
        comments: postCommentsData,
        total: total ? parseInt(total, 10) : 0,
      };
    }

    return {
      comments: [],
      total: 0,
    };
  } catch (error) {
    console.log("could not fetch comments", error);
    return {
      comments: [],
      total: 0,
    };
  }
}

export async function fetchCategory(
  url: string,
): Promise<WPTypes.WPCategoriesResponse> {
  try {
    const fetchResponse = await fetchData<WPTypes.WPCategory>(url);

    const postsData = fetchResponse.responseData;

    if (!isCategoryResponse(postsData)) {
      return {
        categories: [],
        total: 0,
      };
    }

    const total = fetchResponse.responseHeader["x-wp-total"];

    return {
      categories: postsData,
      total: total ? parseInt(total, 10) : 0,
    };
  } catch (error) {
    console.log("could not fetch categories", error);
    return {
      categories: [],
      total: 0,
    };
  }
}

export async function fetchPost(url: string): Promise<WPTypes.WPPostsResponse> {
  try {
    await allCategoriesLoader();

    const fetchResponse = await fetchData<WPTypes.WPPost>(url);
    console.log("fetchResponse post", fetchResponse);

    const postsData = fetchResponse.responseData;

    if (!isPostResponse(postsData)) {
      return {
        posts: [],
        totalPages: 0,
      };
    }

    const totalPages = fetchResponse.responseHeader["x-wp-totalpages"];

    console.log("totalPages from posts", totalPages);
    console.log("totalPages type from posts", typeof totalPages);
    return {
      posts: postsData,
      totalPages: totalPages ? parseInt(totalPages, 10) : 0,
    };
  } catch (error) {
    console.log("could not fetch posts", error);
    return {
      posts: [],
      totalPages: 0,
    };
  }
}

export async function allCategoriesLoader(): Promise<WPTypes.WPCategoriesResponse> {
  const { ecosystemStore } = RootStore;

  const urlPath = "categories";
  const data = await fetchCategory(urlPath);

  await ecosystemStore.saveCategories(data.categories);
  console.log("data categories", data);
  return { categories: data.categories, total: data.total };
}

async function savePostsInfoToStore(
  dataPosts: WPTypes.WPPost[],
  totalPages: number,
) {
  const { ecosystemStore } = RootStore;
  await ecosystemStore.savePosts(dataPosts);
  await ecosystemStore.setTotalPages(totalPages);
}

export async function allPostsLoader(): Promise<WPTypes.WPPostsResponse> {
  const { ecosystemStore } = RootStore;
  const currentPage = ecosystemStore.currentPage;

  const urlPath = "posts/?page=" + currentPage;
  const data = await fetchPost(urlPath);

  console.log("data posts", data);

  await savePostsInfoToStore(data.posts, data.totalPages);
  return { posts: data.posts, totalPages: data.totalPages };
}

export async function categoryIdLoader({ params }: IParams) {
  const { ecosystemStore } = RootStore;
  const currentPage = ecosystemStore.currentPage;
  const { catid = "" } = params;
  const urlPath = `posts/?page=${currentPage}&categories=${catid}`;
  const data = await fetchPost(urlPath);

  await savePostsInfoToStore(data.posts, data.totalPages);

  return { posts: data.posts, totalPages: data.totalPages };
}

export async function searchTermLoader({ params }: IParams) {
  const { ecosystemStore } = RootStore;
  const { term = "" } = params;
  const currentPage = ecosystemStore.currentPage;

  const urlPath = `posts?search=${term.trim()}&page=${currentPage}`;
  const data = await fetchPost(urlPath);

  // await ecosystemStore.setCurrentPage(1);
  await ecosystemStore.setTerm(term.trim());

  await savePostsInfoToStore(data.posts, data.totalPages);

  return { posts: data.posts, totalPages: data.totalPages };
}

export async function postLoader({
  params,
}: IParams): Promise<WPTypes.WPPostResponse> {
  const { slug = "" } = params;
  console.log("post slug", slug);
  console.log("posts params", params);

  const { ecosystemStore } = RootStore;

  const post = await ecosystemStore.getPostBySlug(slug.trim());

  if (post) {
    console.log("post was not fetched yet");
    await ecosystemStore.setPostInfo(post);
    const commentsInfo = await postCommentsLoader(post);

    return { post, comments: commentsInfo.comments };
  }

  const urlPath = "posts/?slug=" + slug;
  const data = await fetchPost(urlPath);

  console.log("data posts", data);

  const { posts } = data;

  if (!posts.length) {
    throw new Response("", {
      status: 404,
      statusText: "Not Found",
    });
  }

  const [fetchedPost] = posts;
  await ecosystemStore.setPostInfo(fetchedPost);

  const commentsInfo = await postCommentsLoader(fetchedPost);

  const res = { post: fetchedPost, comments: commentsInfo.comments };

  return res;
}

export async function postCommentsLoader(
  post: WPTypes.WPPost,
): Promise<WPTypes.WPPostCommentsResponse> {
  const { ecosystemStore } = RootStore;

  const urlPath = "comments?post=" + post.id;
  const data = await fetchPostComments(urlPath);

  await ecosystemStore.savePostComments(data.comments);
  console.log("data comments", data);

  const postComments = data.comments;

  return {
    comments: postComments,
    total: data.total,
  };
}

export async function pageLoader({ params }: IParams) {
  const { slug = "" } = params;
  console.log("page slug", slug);
  return { a: "12" };
}
